import Select from '@components/v2/form/Select/Select';
import Button from '@components/v2/ui/Button/Button';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import Modal from '@components/v2/ui/Modal/Modal';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, SelectChangeEvent, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import TitledMessage from '../../components/TitledMessage';
import useHandleAppException, { AppExceptionResponseData } from '../../hooks/useHandleAppException';
import LangEnum from '../../lang/LangEnum';
import snackbarStore from '../../stores/Snackbar.store';
import useTeceoStyles from '../../useTeceoStyles';
import { useTranslation } from '../translation/TranslationContext';
import DropboxFolderSelection from './DropboxFolderSelection';
import { DropboxConfigurationErrorCode } from './DropboxTypes';
import DropboxConfigurationErrorAlert from './components/DropboxConfigurationErrorAlert/DropboxConfigurationErrorAlert';
import { dropboxRepository } from './dropbox.repository';
import { dropboxOptions } from './options/dropbox-options';
import useDropboxConfig from './useDropboxConfig';

type DropboxConfigProps = {
  configId?: string;
  selectedFolder?: string;
  errorCode: DropboxConfigurationErrorCode | null;
  fileSplitter: string;
  fetchGeneralSettings?: () => void;
};

const DropboxConfig = ({
  configId,
  selectedFolder,
  errorCode,
  fetchGeneralSettings,
  fileSplitter,
}: DropboxConfigProps) => {
  const { classes: teceoClasses } = useTeceoStyles();
  const dropboxConfig = useDropboxConfig();
  const dropboxActions = dropboxRepository();
  const { handleMessage } = snackbarStore;
  const { handleErrorResponse } = useHandleAppException();
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const { translate } = useTranslation();
  const [splitter, setSplitter] = useState<string>(fileSplitter ?? '');
  const requiresOAuth = errorCode === 'OAUTH_REAUTH_REQUIRED';

  useEffect(() => {
    if (!configId || requiresOAuth) {
      return;
    }

    dropboxConfig.synchronizeFolders();
  }, [configId, requiresOAuth, dropboxConfig.synchronizeFolders]);

  const handleSetSelectedFolder = async (folder: string) => {
    const response = await dropboxConfig.setSelectedFolder(folder);
    if (response.ok) {
      await fetchGeneralSettings?.();
      return;
    }

    const fallbackMessage = translate(LangEnum.SNACKBAR_UNABLE_TO_SELECT_FOLDER);
    handleMessage({
      type: 'error',
      message: response.data
        ? handleErrorResponse({
            ok: false,
            data: response.data as AppExceptionResponseData,
          })
        : fallbackMessage,
    });
  };

  const handleRemoveDropboxConfig = async () => {
    const response = await dropboxConfig.removeConfig();
    if (response.ok && fetchGeneralSettings) {
      fetchGeneralSettings();
    }
    setOpenConfirmation(false);
  };

  const handleBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    await dropboxActions.updateFileSplitter(event.target.value);
    fetchGeneralSettings?.();
  };

  return (
    <>
      <Stack spacing={2}>
        {!configId && (
          <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
            <Typography gutterBottom className={teceoClasses.lowercase}>
              {translate(LangEnum.DROPBOX_CONFIGURATION_NOT_FOUND_MESSAGE)}
            </Typography>
            <Button variant="contained" color="primary" onClick={dropboxConfig.initDropboxConfig}>
              {translate(LangEnum.DROPBOX_CONFIGURATION_INIT_BUTTON)}
            </Button>
          </Stack>
        )}

        {configId && (
          <>
            {errorCode && (
              <DropboxConfigurationErrorAlert
                errorCode={errorCode}
                onReconnect={dropboxConfig.initDropboxConfig}
              />
            )}
            <Box>
              {!requiresOAuth && (
                <DropboxFolderSelection
                  loading={dropboxConfig.isLoadingFolders}
                  value={selectedFolder}
                  folders={dropboxConfig.folders}
                  onChange={handleSetSelectedFolder}
                />
              )}
              <TitledMessage
                title={translate(LangEnum.DROPBOX_FILE_SPLITTER_TITLE)}
                message={translate(LangEnum.DROPBOX_FILE_SPLITTER_MESSAGE)}
              />
              <Select
                fullWidth={false}
                label={translate(LangEnum.DROPBOX_FILE_SPLITTER_LABEL)}
                name="separator"
                value={splitter}
                sx={{ width: 200, mt: 1.25 }}
                onBlur={handleBlur}
                onChange={(event: SelectChangeEvent<unknown>) =>
                  setSplitter(event.target.value as string)
                }
              >
                {dropboxOptions.map((option, index) => (
                  <MenuItem key={`${option.value}-${index}`} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
              <Typography gutterBottom>
                {translate(LangEnum.DROPBOX_CONFIGURATION_REMOVE_MESSAGE)}
              </Typography>
              <Button variant="contained" color="primary" onClick={() => setOpenConfirmation(true)}>
                {translate(LangEnum.DROPBOX_CONFIGURATION_REMOVE_BUTTON)}
              </Button>
            </Stack>
          </>
        )}
      </Stack>
      <Modal
        isOpen={openConfirmation}
        size="standard"
        header={{ title: translate(LangEnum.DROPBOX_CONFIGURATION_REMOVE_CONFIRMATION_TITLE) }}
        onClose={() => setOpenConfirmation(false)}
        footer={{
          primaryAction: {
            label: translate(LangEnum.GLOBAL_REMOVE_LABEL),
            onClick: handleRemoveDropboxConfig,
          },
          secondaryAction: {
            label: translate(LangEnum.GLOBAL_CANCEL_LABEL),
            onClick: () => setOpenConfirmation(false),
          },
        }}
      >
        <Typography>
          {translate(LangEnum.DROPBOX_CONFIGURATION_REMOVE_CONFIRMATION_MESSAGE)}
        </Typography>
      </Modal>
    </>
  );
};

export default DropboxConfig;
